/**
 * The reports page's URL vocabulary and its month arithmetic.
 *
 * <p>Months are `YYYY-MM` WALL-CALENDAR keys, stepped as integers and never
 * through `new Date(...)` — a month bucket is not an instant, and the tz-aware
 * formatters would move "2026-07" into June for every reader west of
 * Greenwich (the class `indexTiles/tileBits.monthLabel` already records).</p>
 */

export const REPORT_TAB_KEYS = [
  "buyers",
  "listings",
  "submissions",
  "specialists",
  "advertising",
] as const;

export type ReportTabKey = (typeof REPORT_TAB_KEYS)[number];

const TAB_SET = new Set<string>(REPORT_TAB_KEYS);

/** An unrecognised `?tab=` degrades to the first tab rather than to an empty
 *  page, so a stale bookmark still lands somewhere. */
export function parseReportTab(raw: string | null): ReportTabKey {
  return raw && TAB_SET.has(raw) ? (raw as ReportTabKey) : "buyers";
}

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

export function isMonthKey(raw: string | null | undefined): raw is string {
  return Boolean(raw && MONTH_RE.test(raw));
}

/** The current calendar month in the BROWSER's clock — a default the reader
 *  can change, not a boundary anything is measured against. */
export function currentMonthKey(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

/** Step a month key by `months` (negative steps back), in integer space. */
export function shiftMonthKey(month: string, months: number): string {
  const [y, m] = month.split("-").map(Number);
  const index = y * 12 + (m - 1) + months;
  const year = Math.floor(index / 12);
  const monthOfYear = (index % 12) + 1;
  return `${year}-${String(monthOfYear).padStart(2, "0")}`;
}

/** Inclusive `from..to` as a list of month keys, oldest first. */
export function monthKeysBetween(from: string, to: string): string[] {
  const keys: string[] = [];
  let cursor = from;
  // Bounded by the server's own twelve-month cap; a reversed range yields
  // just `from`, which is what the server does with `to < from` as well.
  for (let i = 0; i < 12 && cursor <= to; i += 1) {
    keys.push(cursor);
    cursor = shiftMonthKey(cursor, 1);
  }
  return keys;
}

/**
 * The advertising period, from the URL or the default of the trailing three
 * months ending now. `to` is clamped to at most eleven months after `from`
 * (the server's twelve-month cap) so a hand-edited URL cannot 400.
 */
export function parsePeriod(
  fromRaw: string | null,
  toRaw: string | null,
  now: Date = new Date(),
): { from: string; to: string } {
  const current = currentMonthKey(now);
  const to = isMonthKey(toRaw) ? toRaw : current;
  const from = isMonthKey(fromRaw) ? fromRaw : shiftMonthKey(to, -2);
  if (from > to) return { from: to, to };
  const cap = shiftMonthKey(from, 11);
  return { from, to: to > cap ? cap : to };
}

/** The months a period picker offers: the last 24, newest first. */
export function periodOptions(now: Date = new Date()): string[] {
  const current = currentMonthKey(now);
  return Array.from({ length: 24 }, (_, i) => shiftMonthKey(current, -i));
}
